import React from 'react'
import { CssBaseline, Snackbar, Alert } from '@mui/material'
import { createTheme, ThemeProvider as MuiThemeProvider, PaletteMode } from '@mui/material/styles'
import type { ThemePreference } from '../api/me'
import { setTheme as persistTheme } from '../api/me'
import useUIStore from '../store/ui'
import { shapeBorderRadius } from '../theme/helpers'

interface ThemeContextValue {
  themePreference: ThemePreference
  resolvedMode: PaletteMode
  setThemePreference: (preference: ThemePreference) => Promise<void>
  isSaving: boolean
}

const ThemePrefContext = React.createContext<ThemeContextValue | undefined>(undefined)

function useSystemMode(): PaletteMode {
  const getInitialMode = React.useCallback(() => {
    if (typeof window === 'undefined') {
      return 'light'
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }, [])

  const [mode, setMode] = React.useState<PaletteMode>(getInitialMode)

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const listener = (event: MediaQueryListEvent) => {
      setMode(event.matches ? 'dark' : 'light')
    }

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', listener)
    } else {
      // Deprecated but kept for Safari < 14 support
      media.addListener(listener)
    }

    return () => {
      if (typeof media.removeEventListener === 'function') {
        media.removeEventListener('change', listener)
      } else {
        media.removeListener(listener)
      }
    }
  }, [])

  return mode
}

export const ThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const themePreference = useUIStore((state) => state.themePreference)
  const setThemePreferenceState = useUIStore((state) => state.setThemePreference)
  const systemMode = useSystemMode()
  const [isSaving, setIsSaving] = React.useState(false)
  const [snackbarMessage, setSnackbarMessage] = React.useState<string | null>(null)

  const resolvedMode: PaletteMode = themePreference === 'system' ? systemMode : themePreference

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: resolvedMode,
          primary: {
            main: resolvedMode === 'dark' ? '#9bcaff' : '#3355ff',
          },
          secondary: {
            main: resolvedMode === 'dark' ? '#ffb2a6' : '#ff5d7d',
          },
          background: {
            default: resolvedMode === 'dark' ? '#0f172a' : '#f8f9ff',
            paper: resolvedMode === 'dark' ? '#1b2138' : '#ffffff',
          },
        },
        shape: {
          borderRadius: 16,
        },
        typography: {
          fontFamily: '"Roboto", "Noto Sans", "Helvetica Neue", Arial, sans-serif',
          fontSize: 14,
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              ':root': {
                '--rpa-scrollbar-track':
                  resolvedMode === 'dark' ? 'rgba(148, 163, 184, 0.18)' : 'rgba(15, 23, 42, 0.08)',
                '--rpa-scrollbar-thumb':
                  resolvedMode === 'dark' ? 'rgba(148, 163, 184, 0.55)' : 'rgba(15, 23, 42, 0.3)',
                '--rpa-scrollbar-thumb-hover':
                  resolvedMode === 'dark' ? 'rgba(148, 163, 184, 0.7)' : 'rgba(15, 23, 42, 0.42)',
                '--rpa-scrollbar-thumb-active':
                  resolvedMode === 'dark' ? 'rgba(148, 163, 184, 0.85)' : 'rgba(15, 23, 42, 0.55)',
              },
              '*': {
                scrollbarWidth: 'thin',
                scrollbarColor: 'var(--rpa-scrollbar-thumb) var(--rpa-scrollbar-track)',
              },
              '*::-webkit-scrollbar': {
                width: '10px',
                height: '10px',
              },
              '*::-webkit-scrollbar-track': {
                backgroundColor: 'var(--rpa-scrollbar-track)',
              },
              '*::-webkit-scrollbar-thumb': {
                backgroundColor: 'var(--rpa-scrollbar-thumb)',
                borderRadius: '999px',
                border: '2px solid var(--rpa-scrollbar-track)',
              },
              '*::-webkit-scrollbar-thumb:hover': {
                backgroundColor: 'var(--rpa-scrollbar-thumb-hover)',
              },
              '*::-webkit-scrollbar-thumb:active': {
                backgroundColor: 'var(--rpa-scrollbar-thumb-active)',
              },
            },
          },
          MuiAppBar: {
            defaultProps: {
              elevation: 0,
              color: 'default',
            },
            styleOverrides: {
              root: {
                backgroundImage: 'none',
                borderBottom: resolvedMode === 'dark' ? '1px solid rgba(148, 163, 184, 0.2)' : '1px solid rgba(15, 23, 42, 0.08)',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: ({ theme }) => ({
                borderRadius: shapeBorderRadius(theme),
                '&.rpa-prepFilterBar': {
                  padding: 0,
                },
              }),
            },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                backgroundImage: 'none',
              },
            },
          },
        },
      }),
    [resolvedMode],
  )

  const setThemePreference = React.useCallback(
    async (preference: ThemePreference) => {
      const previous = useUIStore.getState().themePreference
      if (previous === preference) {
        return
      }
      setThemePreferenceState(preference)
      setIsSaving(true)
      try {
        await persistTheme(preference)
      } catch (error) {
        setThemePreferenceState(previous)
        const message =
          error instanceof Error && error.message
            ? error.message
            : 'Unable to save theme preference. Please try again.'
        setSnackbarMessage(message)
        throw error
      } finally {
        setIsSaving(false)
      }
    },
    [setThemePreferenceState],
  )

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setSnackbarMessage(null)
  }

  const contextValue = React.useMemo<ThemeContextValue>(
    () => ({ themePreference, resolvedMode, setThemePreference, isSaving }),
    [themePreference, resolvedMode, setThemePreference, isSaving],
  )

  return (
    <ThemePrefContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
        <Snackbar
          open={Boolean(snackbarMessage)}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </MuiThemeProvider>
    </ThemePrefContext.Provider>
  )
}

export function useThemePref(): ThemeContextValue {
  const context = React.useContext(ThemePrefContext)
  if (!context) {
    throw new Error('useThemePref must be used within ThemeProvider')
  }
  return context
}
